import * as React from "react"
import { useEffect, useRef, useState } from "react"
import { AppState, getMetaData } from "../data/redux-state"
import * as d3 from "d3"
import "./ChartUI.css"
import waterMarkImage from "../assets/images/WEGC-UniGraz_Logo_medium.png"
import { useIntl } from "gatsby-plugin-intl"

import {
  DPoint,
  extractDataLines,
  UncertaintyLine,
} from "../utils/extractLines"
import generateLine from "../utils/generateLine"
import CSVDownloadButton from "./csvDownloadButton"
import { AboveChart } from "./AboveChart"
import {
  XRangeSlider,
  YRangeSlider,
  updateChartAfterZoomEvent,
} from "./XYRange"
import { TooltipClickOnDataPoints } from "./TooltipClickOnDataPoints"
import { downloadChartPng } from "../utils/download-png"
import { useChartDisplayState } from "./useChartDisplayState"
import {
  getDataFontSize,
  getLabelFontSize,
  getXLabel,
  getYLabel,
  getYLabelHorizontalDistance,
} from "./chart/chartLabels"
import { splitIntoContiguousYearSegments } from "./chart/chartDataUtils"
import { drawLegendOverlay } from "./chart/drawLegendOverlay"
// import { useWindowSize } from "../utils/useWindowSize"
const LEGEND_BOX_SIDE_INSET = 10
const CLICKED_DOT_CLASS = "clickedDot"

export const ChartUI = (props: AppState) => {
  const chartClass = "basicLineChart"
  const svgRef = useRef(null)
  const ref = useRef<HTMLDivElement | null>(null)

  const downloadScreenshot = async () => {
    const screenshotTarget = ref.current
    if (!(screenshotTarget instanceof HTMLElement)) {
      return
    }

    await downloadChartPng({
      appState: props,
      screenshotTarget,
    })
  }

  const [clickedDataArray, setClickedDataArray] = useState<Array<DPoint>>([])
  const [fixedSvgHeight, setFixedSvgHeight] = useState<number | null>(null)
  const intl = useIntl()
  const {
    data,
  }: {
    data: UncertaintyLine[]
  } = extractDataLines(props)
  const {
    hasData,
    displayDataXRange,
    displayXRange,
    displayDataYRange,
    displayYRange,
    displayAboveChartXrange,
    emptyChartMessage,
  } = useChartDisplayState({
    appState: props,
    data,
    intl,
  })

  const metaData = getMetaData(props)

  useEffect(() => {
    const oldSvg = d3.select("." + chartClass)
    oldSvg.selectAll("*").remove()
    oldSvg
      .style("background-image", "none")
      .style("background-size", null)
      .style("background-position", null)
      .style("background-repeat", null)

    if (
      !displayDataXRange ||
      !displayXRange ||
      !displayDataYRange ||
      !displayYRange ||
      !displayAboveChartXrange
    ) {
      setClickedDataArray((prevState) => (prevState.length === 0 ? prevState : []))
      return
    }

    // @ts-ignore
    const boundingClientRect = svgRef?.current?.getBoundingClientRect()
    const svgWidth = boundingClientRect.width ?? window.innerWidth
    const svgHeight = boundingClientRect.height ?? window.innerWidth / 2
    setFixedSvgHeight((prevHeight) => (prevHeight === svgHeight ? prevHeight : svgHeight))

    const yAxisWidth = Math.floor(svgWidth * 0.15)
    const rightSpace = Math.floor(svgWidth * 0.1)
    const xAxisHeight = Math.floor(svgHeight * 0.1)

    const lineWidth = svgWidth - yAxisWidth - rightSpace
    const lineHeight = svgHeight - xAxisHeight
    const textOffsetY = 20
    const xOffsetDatSrcText = yAxisWidth + lineWidth * 0.146

    const zoom = d3
      .zoom()
      .on("zoom", (event) => {
        updateChartAfterZoomEvent(
          event,
          xScale,
          yScale,
          displayDataXRange,
          displayDataYRange
        )
      })
      .translateExtent([
        [0, 0],
        [svgWidth, svgHeight],
      ])
      .scaleExtent([0.5, 8])

    const centeredOverlayX = svgWidth * 0.15 + LEGEND_BOX_SIDE_INSET
    const centeredOverlayWidth = svgWidth * 0.75 - LEGEND_BOX_SIDE_INSET * 2

    // Add a clipPath: everything out of this area won't be drawn.

    const svg = d3
      .select("." + chartClass)
      .append("svg")
      .attr("width", svgWidth)
      .attr("height", svgHeight)
      .style("pointer-events", "visible")
      .call(zoom as any) // Register zoom only after click-event
      .append("g")

    let clip = svg
      .append("defs")
      .append("SVG:clipPath")
      .attr("id", "clip")
      .append("SVG:rect")
      .attr("width", lineWidth)
      .attr("height", svgHeight)
      .attr("x", yAxisWidth)
      .attr("y", -xAxisHeight)

    const xScale = d3
      .scaleTime()
      .domain(displayXRange)
      .range([yAxisWidth, yAxisWidth + lineWidth])

    const labelFontSize = getLabelFontSize()
    const xAxis = d3.axisBottom(xScale)
    svg
      .append("g")
      .attr("transform", `translate(0, ${lineHeight})`)
      .style("font-size", labelFontSize + "px")
      .attr("id", "x-axis")
      .call(
        xAxis.tickFormat(((d: Date, i: number) => {
          if (svgWidth < 690) {
            if (
              i === 0 ||
              d.getFullYear() == displayXRange[displayXRange.length - 1].getFullYear()
            ) {
              return new Date(d).getFullYear() + ""
            }
          } else {
            return new Date(d).getFullYear() + ""
          }
        }) as any)
      )
      .call((g) =>
        g
          .append("text")
          .attr("x", yAxisWidth + lineWidth * 0.5)
          .attr("y", 1.8 * textOffsetY)
          .attr("fill", "currentColor")
          .text(getXLabel(props, intl))
          .style("font-size", labelFontSize + "px")
      )

    const yScale = d3.scaleLinear().domain(displayYRange).range([lineHeight, 0])
    const yLabelX = getYLabelHorizontalDistance(displayYRange)
    const yLabelY = lineHeight * 0.5
    const yAxis = d3.axisLeft(yScale)
    svg
      .append("g")
      .attr("transform", `translate(${yAxisWidth}, 0)`)
      .style("font-size", labelFontSize + "px")
      .attr("id", "y-axis")
      .call(
        yAxis.tickFormat((d) => {
          return d + ""
        })
      )
      .call((g) =>
        g
          .append("text")
          .attr("x", yLabelX)
          .attr("y", yLabelY)
          .attr("fill", "currentColor")
          .attr("text-anchor", "middle")
          .attr("transform", "rotate(-90 " + yLabelX + " " + yLabelY + ")")
          .text(getYLabel(props, intl))
          .style("font-size", labelFontSize + "px")
      )

    svg
      .append("g")
      .attr("class", "tickLines")
      .attr("transform", "translate(" + 0 + "," + lineHeight + ")")
      .call(
        xAxis
          .ticks(5) // major grid x-axis
          .tickSize(-lineHeight)
          .tickFormat(() => "")
      )
    svg
      .append("g")
      .attr("class", "tickLines")
      .attr("transform", "translate(" + 0 + "," + lineHeight + ")")
      .call(
        xAxis
          .ticks(50) // minor tickmarks x-axis
          .tickSize(-lineHeight / 100)
          .tickFormat(() => "")
      )

    svg
      .append("g")
      .attr("class", "tickLines")
      .attr("transform", "translate(" + yAxisWidth + "," + 0 + ")")
      .call(
        yAxis
          .ticks(6) // major grid y-axis
          .tickSize(-lineWidth)
          .tickFormat(() => "")
      )
    svg
      .append("g")
      .attr("class", "tickLines")
      .attr("transform", "translate(" + yAxisWidth + "," + 0 + ")")
      .call(
        yAxis
          .ticks(60) // minor tickmarks y-axis
          .tickSize(-lineWidth / 200)
          .tickFormat(() => "")
      )

    const waterMarkX = 1.9 + (yAxisWidth / svgWidth) * 100
    d3.select("." + chartClass)
      .style("background-image", `url(${waterMarkImage})`)
      .style("background-size", "11% auto")
      .style("background-position", waterMarkX + "% 88.5%")
      .style("background-repeat", "no-repeat")

    const datSrcFontSize = getDataFontSize()
    svg
      .append("g")
      .attr("transform", `translate(0, ${lineHeight})`)
      .style("font-size", datSrcFontSize + "px")
      .call((g) =>
        g
          .append("text")
          .attr("x", xOffsetDatSrcText)
          .attr("y", -lineHeight * 0.045)
          .attr("text-anchor", "start")
          .attr("font-weight", 100)
          .text("GCCI data provider: " + (metaData?.ProjectDataProvider ?? "N/A"))
      )
      .call((g) =>
        g
          .append("text")
          .attr("x", xOffsetDatSrcText)
          .attr("y", -lineHeight * 0.02)
          .attr("text-anchor", "start")
          .attr("font-weight", 100)
          .text("DatSrc: " + (metaData?.DataSourcesShortcite ?? metaData?.DataSourcesShortCite ?? "N/A"))
      )

    // Legend background rect drawn inside SVG: sits above grid but below data,
    // so grid lines are hidden behind it while data lines/dots remain in front.
    drawLegendOverlay({
      svg,
      centeredOverlayX,
      centeredOverlayWidth,
      svgWidth,
      data,
      appState: props,
      intl,
    })

    const chartBody = svg.append("g").attr("clip-path", "url(#clip)")

    for (let i = 0; i < data.length; i++) {
      if (data[i]) {
        const line = data[i]
        const color = line.color
        const connectorStrokeScale =
          props.indicatorClass.startsWith("EWM")
          && line.coVar.Metadata.SymbolSize === "small"
            ? 0.6
            : 1

        if (line.coVarOption?.urEnabled && line.urPoints && line.urPoints.length > 0) {
          const urSegments = splitIntoContiguousYearSegments(line.urPoints)
          for (const urSegment of urSegments) {
            chartBody
              .append("path")
              .datum(urSegment)
              .attr("fill", color)
              .attr("opacity", 0.3)
              .attr("stroke", "none")
              .attr("class", "area")
              .attr(
                "d",
                (d3.area() as any)
                  .x(function (d: DPoint) {
                    return xScale(d.date)
                  } as any)
                  .y0(function (d: DPoint) {
                    return yScale(d.up!.value)
                  } as any)
                  .y1(function (d: DPoint) {
                    return yScale(d.down!.value)
                  } as any)
              )
          }
        }

        // Render sdev as shaded area + connected lines for StDevAvsD coVar
        const isSdevCircleMode = line.coVarOption?.sdevEnabled
          && line.sDevPoints && line.sDevPoints.length > 0
          && line.coVarKey.includes("StDevAvsD")
        if (isSdevCircleMode) {
          // Shaded area between upper and lower bounds
          chartBody
            .append("path")
            .datum(line.sDevPoints)
            .attr("fill", color)
            .attr("opacity", 0.3)
            .attr("stroke", "none")
            .attr("class", "area")
            .attr(
              "d",
              (d3.area() as any)
                .defined(function (d: DPoint) {
                  return !isNaN(d.up!.value) && !isNaN(d.down!.value)
                } as any)
                .x(function (d: DPoint) {
                  return xScale(d.date)
                } as any)
                .y0(function (d: DPoint) {
                  return yScale(d.up!.value)
                } as any)
                .y1(function (d: DPoint) {
                  return yScale(d.down!.value)
                } as any)
            )
          // Upper bound connected line
          chartBody
            .append("svg:path")
            .datum(line.sDevPoints)
            .attr("fill", "none")
            .attr("stroke", color)
            .attr("stroke-width", 1.0 * connectorStrokeScale)
            .attr("class", "line")
            .attr(
              "d",
              // @ts-ignore
              d3
                .line()
                .defined(((d: DPoint) => !isNaN(d.up!.value)) as any)
                .x(((d: DPoint) => xScale(d.date)) as any)
                .y(((d: DPoint) => yScale(d.up!.value)) as any)
            )
          // Lower bound connected line
          chartBody
            .append("svg:path")
            .datum(line.sDevPoints)
            .attr("fill", "none")
            .attr("stroke", color)
            .attr("stroke-width", 1.0 * connectorStrokeScale)
            .attr("class", "line")
            .attr(
              "d",
              // @ts-ignore
              d3
                .line()
                .defined(((d: DPoint) => !isNaN(d.down!.value)) as any)
                .x(((d: DPoint) => xScale(d.date)) as any)
                .y(((d: DPoint) => yScale(d.down!.value)) as any)
            )
        }

        chartBody
          .append("svg:path")
          // .attr("clip-path", "url(#clip)")
          .datum(line.unfilteredPoints ?? line.mainPoints)
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 2.8 * connectorStrokeScale)
          .attr("class", "line")
          .attr(
            "d",
            // @ts-ignore
            d3
              .line()
              .defined(((d: DPoint) => !isNaN(d.value)) as any)
              .x(((d: DPoint) => {
                return xScale(d.date)
              }) as any)
              .y(((d: DPoint) => {
                return yScale(d.value)
              }) as any)
          )

        generateLine({
          props,
          index: i,
          svg,
          data,
          xScale,
          yScale,
          d3,
          clickDot,
          clickedDataArray,
          chartBody,
        })
      }
    }

    function removeClickedDots() {
      setClickedDataArray((prevState) => (prevState.length === 0 ? prevState : []))
    }

    async function clickDot(
      event: any,
      data: UncertaintyLine[],
      xScale: d3.ScaleTime<number, number, never>,
      yScale: d3.ScaleLinear<number, number, never>,
      line: UncertaintyLine,
      svg: any,
      clickedDataArray: DPoint[],
      points: DPoint[]
    ) {
      if (!data.length) {
        return
      }

      const clickXScaled = d3.pointer(event)[0]

      const clickX = xScale.invert(clickXScaled)

      const bisectDate = d3.bisector(function (d: DPoint) {
        return d.date
      })

      let dataPointIndex = bisectDate.center(points, clickX)

      let dataPoint: DPoint = points[dataPointIndex]

      if (!dataPoint) {
        return
      }
      const yScaled = yScale(dataPoint.value)
      const timeScaled = xScale(dataPoint.date)

      let newDot = {
        dataPoint,
        click: [timeScaled, yScaled + 20],
        removeClickedDots,
        xScale,
        yScale,
        event,
        line,
        appState: props,
        timeScaled,
        yScaled,
      }

      setClickedDataArray((prevState: any) => [...prevState, newDot])
    }

    removeClickedDots()
  }, [
    hasData,
    displayDataXRange,
    displayXRange,
    displayDataYRange,
    displayYRange,
    displayAboveChartXrange,
    props.geoRegion,
    props.geoVariable,
    props.indicatorClass,
    props.sourceJson,
    props.subVariable,
    props.coVarOptions,
    props.xRange,
    props.yRange,
  ])

  // Draw the clicked-point rings into the chart SVG itself so they share its
  // coordinate space on screen and in the PNG export. A container-anchored
  // overlay drifts upward by the AboveChart height.
  useEffect(() => {
    const svg = d3.select("." + chartClass)
    svg.selectAll("." + CLICKED_DOT_CLASS).remove()
    clickedDataArray.forEach((dot: any) => {
      svg
        .append("circle")
        .attr("class", CLICKED_DOT_CLASS)
        .attr("r", 6)
        .attr("cx", dot.timeScaled)
        .attr("cy", dot.yScaled)
        .attr("stroke", "#FF0000")
        .attr("fill", "none")
    })
  }, [clickedDataArray])

  let pngDownloadButton = (
    <div className="pngDownloadButton" onClick={downloadScreenshot}>
      {intl.formatMessage({ id: "dropdown.downloadChartAsPng" })}
    </div>
  )

  let tooltips = clickedDataArray.map((data: DPoint, index: number) => {
    return (
      <TooltipClickOnDataPoints
        key={index}
        index={index}
        {...data}
        clickedDataArray={clickedDataArray}
      />
    )
  })

  return (
    <>
      <div
        ref={ref}
        id={"screenshotcontainer"}
        style={{
          height: "100%",
          paddingBottom: "24px", // workaround for screenshot to include xAxis title
          position: "relative",
        }}
      >
        {hasData && displayAboveChartXrange && (
          <AboveChart data={data} appState={props} xRange={displayAboveChartXrange} />
        )}
        <svg
          style={{ width: "100%", height: fixedSvgHeight ?? "100%", position: "relative", zIndex: 1 }}
          ref={svgRef}
          className={chartClass}
          id="allChart"
        />
        {!hasData && (
          <div className="chartEmptyState">
            {emptyChartMessage}
          </div>
        )}
        {displayDataXRange && displayXRange && (
          <XRangeSlider
            dataXRange={displayDataXRange}
            xRange={displayXRange}
            style={{ width: "100%" }}
          />
        )}
        {displayDataYRange && displayYRange && (
          <YRangeSlider
            dataYRange={displayDataYRange}
            yRange={displayYRange}
            style={{ width: "100%" }}
          />
        )}
        {hasData && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              flexWrap: "wrap",
            }}
          >
            {pngDownloadButton}
            {/* {csvDownloadButton} */}
            <CSVDownloadButton {...props} />
          </div>
        )}
        {clickedDataArray.length > 0 ? tooltips : null}
      </div>
    </>
  )
}
